import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { RevealLines } from "@/components/motion";

const HERO_TEXTURE =
  "https://images.unsplash.com/photo-1545873509-33e944ca7655?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBhYnN0cmFjdCUyMGdvbGQlMjB0ZXh0dXJlfGVufDB8fHx8MTc4NTk5NDgzNHww&ixlib=rb-4.1.0&q=85";

export const Hero = ({ ready }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      className="relative min-h-screen flex flex-col justify-center overflow-hidden"
      data-testid="hero"
    >
      <motion.div
        style={{ y }}
        className="absolute inset-0 pointer-events-none"
      >
        <img
          src={HERO_TEXTURE}
          alt=""
          className="absolute right-0 top-0 w-[55%] h-full object-cover opacity-[0.14] mix-blend-multiply"
        />
        <div className="absolute -right-32 top-1/4 w-[500px] h-[500px] rounded-full bg-[#C5A059]/10 blur-[120px]" />
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="relative max-w-[1400px] mx-auto w-full px-6 md:px-10 pt-28"
      >
        {ready && (
          <>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9, duration: 1 }}
              className="text-xs uppercase tracking-[0.35em] text-[#C5A059] mb-8"
            >
              — Aurelume Branding Solutions LLP · MSME Certified
            </motion.p>

            <h1 className="font-serif font-light text-[9vw] md:text-[6.4vw] leading-[0.98] tracking-[-0.02em] text-[#1A1713] max-w-[15ch]">
              <RevealLines lines={["Category leaders"]} delay={0.3} />
              <RevealLines lines={["aren't discovered."]} delay={0.42} />
              <RevealLines
                lines={[
                  <span key="p">
                    They&rsquo;re{" "}
                    <span className="italic text-[#C5A059]">built</span> —
                  </span>,
                ]}
                delay={0.54}
              />
              <RevealLines lines={["deliberately, from"]} delay={0.66} />
              <RevealLines
                lines={[<em key="b" className="italic">the inside out.</em>]}
                delay={0.78}
              />
            </h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3, duration: 1 }}
              className="mt-12 max-w-md ml-auto md:mr-4"
            >
              <p className="text-base md:text-lg text-[#1A1713]/70 leading-relaxed">
                We go beyond logos and colours — crafting powerful brand
                personalities that connect, communicate and build lasting trust.
              </p>
            </motion.div>
          </>
        )}
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={ready ? { opacity: 1 } : {}}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-6 md:left-10 flex items-center gap-3 text-[#78716C]"
      >
        <ArrowDown size={16} className="animate-bounce" />
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll to explore</span>
      </motion.div>
    </section>
  );
};
